import { Avatar, Button, Card, Flex, List, Progress, theme } from 'antd';
import { LuCheck, LuChevronRight, LuCircleHelp, LuInfo, LuLock, LuPlus } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';
import { PROJECT_TYPE_LABELS } from '@utils/constants';
import { isSystemNode } from '@utils/taxonomyTree';

import { TRAINING_GATE } from '../../constants';
import { countsUnavailableReason, moreOf, sampleUnitOf } from '../../objectTree';
import { formatCount } from './objectsUtils';

const getTitle = (object, unit, { status, annotations, needsNewAnnotations }) => {
	if (status === TRAINING_GATE.READY) return `Train new object from ${object?.name ?? ''}`;
	if (status === TRAINING_GATE.UNKNOWN) return countsUnavailableReason(object);
	const retrain = isSystemNode(object) ? 'train' : 're-train';
	if (needsNewAnnotations) return `Annotate new examples to ${retrain}`;
	// Annotations met but still locked: only the tiles are short, and more
	// annotations on the same tiles would not unlock it.
	if (annotations.met) return `Annotate more ${unit}s to ${retrain}`;
	return `Need more annotations to ${retrain}`;
};

// "Annotate 6 more “Spruce” on 5 more tiles." — only what is still missing.
const getAnnotateStep = (name, unit, { annotations, tiles, needsNewAnnotations }) => {
	if (needsNewAnnotations) return `Annotate new “${name}” examples.`;
	const onTiles = tiles?.missing > 0 ? ` on ${moreOf(tiles.missing, unit)}` : '';
	return annotations.missing > 0
		? `Annotate ${formatCount(annotations.missing)} more “${name}”${onTiles}.`
		: `Annotate “${name}”${onTiles}.`;
};

// Accent and border are token names, read from the theme at render.
const STATUS_LOOK = {
	[TRAINING_GATE.READY]: {
		accent: 'colorPrimary',
		border: 'colorPrimaryBorder',
		Icon: LuCheck,
		titleType: 'success',
	},
	[TRAINING_GATE.LOCKED]: {
		accent: 'colorWarning',
		border: 'colorWarningBorder',
		Icon: LuLock,
		titleType: 'warning',
	},
	[TRAINING_GATE.UNKNOWN]: {
		accent: 'colorTextQuaternary',
		border: 'colorBorder',
		Icon: LuCircleHelp,
		titleType: 'secondary',
	},
};

// Not detected in this project type: nothing to work towards, so neutral rather
// than a warning.
const NOT_DETECTED_LOOK = {
	accent: 'colorTextQuaternary',
	border: 'colorBorder',
	Icon: LuInfo,
	titleType: 'secondary',
};

/** One count the server checks, against its threshold. */
const RequirementRow = ({ label, requirement, color }) => {
	const { token } = theme.useToken();
	const { count, required, met, percent } = requirement;

	return (
		<div>
			<Flex justify="space-between" align="center">
				<BaseText type="secondary" size="sm">
					{label}
				</BaseText>
				<Flex align="center" gap={token.sizeXXS}>
					{met && <LuCheck size={12} color={token.colorSuccess} aria-hidden="true" />}
					<BaseText type={met ? 'success' : 'secondary'} size="xs">
						{formatCount(count)} / {formatCount(required)}
					</BaseText>
				</Flex>
			</Flex>
			<Progress
				percent={percent}
				showInfo={false}
				size="small"
				strokeColor={met ? token.colorSuccess : color}
				aria-label={`${label}: ${formatCount(count)} of ${formatCount(required)}`}
			/>
		</div>
	);
};

/**
 * Card that tells the user whether a child object can be trained from the
 * selected node, and what to do if not. The states come from `getTrainingGate`:
 *   - ready:  "Train Object" starts the create form
 *   - locked: numbered unlock steps and "Select project"
 *   - unknown: the summary has not loaded (or failed) — no counts or steps, but
 *     "Select project" stays, as annotating is a valid next step either way
 * Without a model for the project type (`is_available` false) the card says only
 * that Biodrone does not detect the object there: no counts (they cannot grow), no
 * steps and no "Select project", as annotation happens on an object's own tiles
 * and sending the user to a project would end in a refusal.
 * This comes first, before any state: the summary's counts span every project
 * type, so they can call an object ready where it is not detected.
 * The UI says "detect", never "model": clients are not expected to know the term.
 * Ready and locked list each count against its threshold: annotations, and tiles
 * (images on a single-image project). A locked node with both met adds a row for
 * what is left: new annotations since the last training.
 *
 * @param {object} object - the selected taxonomy node
 * @param {{ status: string, annotations: object | null, tiles: object | null, needsNewAnnotations: boolean }} gate
 * @param {string} mode - the selected project type, which names the tiles
 * @param {() => void} onTrain - "Train Object" pressed (ready state only)
 * @param {() => void} onSelectProject - "Select project" pressed (locked and unknown states)
 */
const TrainingDetailCard = ({ object, gate, mode, onTrain, onSelectProject }) => {
	const { token } = theme.useToken();

	const { status, annotations, tiles, needsNewAnnotations } = gate;
	const isReady = status === TRAINING_GATE.READY;
	const isLocked = status === TRAINING_GATE.LOCKED;
	// The same test as the tree's "Not in …" tag, so the two cannot disagree.
	const hasModel = Boolean(object?.is_available);
	const look = hasModel
		? (STATUS_LOOK[status] ?? STATUS_LOOK[TRAINING_GATE.UNKNOWN])
		: NOT_DETECTED_LOOK;
	const { Icon, titleType } = look;
	const accent = token[look.accent];
	const name = object?.name ?? '';
	const unit = sampleUnitOf(mode);
	const unitLabel = unit === 'image' ? 'Images' : 'Tiles';
	const projectType = PROJECT_TYPE_LABELS[mode];
	const projectTypeName = projectType ? `${projectType} projects` : 'this project type';

	const unlockSteps = isLocked &&
		hasModel && [
			'Open a project.',
			getAnnotateStep(name, unit, gate),
			'Create from there, or return here.',
		];

	return (
		<Card size="small" style={{ borderColor: token[look.border] }}>
			<Flex vertical gap={12}>
				<Flex align="center" gap={8}>
					<Avatar
						size={24}
						icon={<Icon size={13} aria-hidden="true" />}
						style={{ backgroundColor: accent, flex: 'none' }}
					/>
					<BaseText strong type={titleType}>
						{hasModel
							? getTitle(object, unit, gate)
							: `Not available in ${projectTypeName}`}
					</BaseText>
				</Flex>

				{hasModel && annotations && (
					<Flex vertical gap={8}>
						<RequirementRow
							label="Annotations"
							requirement={annotations}
							color={accent}
						/>
						{tiles && (
							<RequirementRow label={unitLabel} requirement={tiles} color={accent} />
						)}
						{needsNewAnnotations && (
							<Flex justify="space-between">
								<BaseText type="secondary" size="sm">
									New annotations since the last training
								</BaseText>
								<BaseText type="warning" size="xs">
									None yet
								</BaseText>
							</Flex>
						)}
					</Flex>
				)}

				{hasModel && isReady && (
					<Button type="primary" icon={<LuPlus aria-hidden="true" />} onClick={onTrain}>
						Train Object
					</Button>
				)}

				{!hasModel && (
					<BaseText type="secondary" size="sm">
						“{name}” detection is not available in {projectTypeName} yet, so it can’t be
						annotated there.
					</BaseText>
				)}

				{!isReady && hasModel && (
					<Flex vertical gap={8} align="flex-start">
						{unlockSteps && (
							<List
								size="small"
								split={false}
								dataSource={unlockSteps}
								renderItem={(step, index) => (
									<List.Item>
										<Flex align="flex-start" gap={8}>
											<Avatar size={18}>
												{/* Block, or the small number sits on the avatar's larger-font baseline, 2px low. */}
												<BaseText size="xxs" style={{ display: 'block' }}>
													{index + 1}
												</BaseText>
											</Avatar>
											<BaseText type="secondary" size="sm">
												{step}
											</BaseText>
										</Flex>
									</List.Item>
								)}
							/>
						)}
						<Button
							icon={<LuChevronRight aria-hidden="true" />}
							iconPlacement="end"
							onClick={onSelectProject}>
							Select project
						</Button>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};

export default TrainingDetailCard;
